import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config';
import { getToken } from '../auth';
import { api as authApi } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useAlert } from '../hooks/useAlert';
import { SkeletonForm } from '../components/Skeleton';
import {
  PageHeader, SectionLabel, Chip, AmountChip, SummaryCard, SummaryRow, PayButton, PleaseWait, Dropdown,
} from '../components/Form';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Icon from '../components/Icon';

const AMOUNT_OPTIONS = [5, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

interface Game { id: string; name: string; credentialRequestsEnabled?: boolean; }
interface PaymentMethod { id: string; name: string; paymentTag: string | null; logoUrl: string | null; }
interface PaymentAccount { id: string; label: string; deepLink: string; }

const WALLET_OPTION = { id: '__wallet__', name: 'Wallet', paymentTag: null, logoUrl: null } as PaymentMethod;

function allowsCredentialRequests(game: Game) {
  return game.credentialRequestsEnabled !== false;
}

export default function TopUp() {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { alert } = useAlert();
  const [games, setGames] = useState<Game[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [walletBalance, setWalletBalance] = useState<number>(0);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(WALLET_OPTION);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPleaseWait, setShowPleaseWait] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payAccount, setPayAccount] = useState<PaymentAccount | null>(null);
  const [showGamePicker, setShowGamePicker] = useState(false);
  const [walletError, setWalletError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [gamesRes, methodsRes] = await Promise.allSettled([
          axios.get<Game[]>(`${API_BASE}/games`),
          axios.get<PaymentMethod[]>(`${API_BASE}/payment-methods`),
        ]);
        if (gamesRes.status === 'fulfilled') {
          const credentialGames = gamesRes.value.data.filter(allowsCredentialRequests);
          setGames(credentialGames);
        }
        if (methodsRes.status === 'fulfilled') setPaymentMethods(methodsRes.value.data);
        if (gamesRes.status === 'rejected' || methodsRes.status === 'rejected') {
          alert('Load Warning', 'Some top-up options could not be loaded. Please try again in a moment.');
        }
      } catch {
        alert('Error', 'Failed to prepare top-up. Please try again.');
      } finally {
        setLoading(false);
      }
      authApi.get<{ balance: number }>('/wallet/me')
        .then(res => setWalletBalance(res.data.balance || 0))
        .catch(() => null);
    })();
  }, [alert]);

  const isWallet = selectedMethod?.id === WALLET_OPTION.id;

  const handlePayNow = async () => {
    const token = await getToken();
    if (!token) return alert('Not Signed In', 'Please sign in to continue.');
    if (!selectedGame) return alert('Missing Info', 'Please select a game.');
    if (!selectedMethod) return alert('Missing Info', 'Please select a payment method.');
    if (!selectedAmount) return alert('Select Amount', 'Please select a top-up amount.');

    if (isWallet) {
      const balanceCents = Math.round(walletBalance * 100);
      const amountCents = Math.round(selectedAmount * 100);
      if (balanceCents < amountCents) {
        setWalletError(`Your wallet has $${walletBalance.toFixed(2)}. Top it up or pick a smaller amount.`);
        return;
      }
      setWalletError('');
      setSubmitting(true);
      const previousBalance = walletBalance;
      setWalletBalance(b => Math.round(b * 100 - amountCents) / 100);
      try {
        await axios.post(
          `${API_BASE}/orders/topup`,
          { gameId: selectedGame.id, amount: selectedAmount, target: 'game', paymentSource: 'wallet' },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        authApi.get<{ balance: number }>('/wallet/me')
          .then(res => setWalletBalance(res.data.balance || 0))
          .catch(() => null);
        setSelectedAmount(null);
        setShowPleaseWait(true);
        setTimeout(() => {
          setShowPleaseWait(false);
          navigate('/profile?openHistory=1');
        }, 2200);
      } catch (err: any) {
        setWalletBalance(previousBalance);
        const data = err?.response?.data;
        const message = data?.code === 'INSUFFICIENT_WALLET_BALANCE'
          ? `Your wallet has $${Number(data?.balance || 0).toFixed(2)}. Top it up before paying from wallet.`
          : data?.message || 'Could not submit order.';
        if (data?.code === 'INSUFFICIENT_WALLET_BALANCE') setWalletError(message);
        else alert('Order Error', message);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.get<PaymentAccount>(
        `${API_BASE}/payment-methods/${selectedMethod.id}/random-account`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPayAccount(res.data);
      setShowPayModal(true);
    } catch (err: any) {
      alert('No Payment Account', err?.response?.data?.message || 'This payment method has no active accounts set up yet.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmPay = async () => {
    if (!payAccount || !selectedGame || !selectedMethod || !selectedAmount) return;
    const token = await getToken();
    if (!token) return;
    setShowPayModal(false);

    try {
      await axios.post(
        `${API_BASE}/orders/topup`,
        {
          gameId: selectedGame.id,
          paymentMethodId: selectedMethod.id,
          paymentAccountId: payAccount.id,
          amount: selectedAmount,
          target: 'game',
          paymentSource: 'method',
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
    } catch (err: any) {
      alert('Order Error', err?.response?.data?.message || 'Order could not be recorded.');
      return;
    }

    try { window.open(payAccount.deepLink, '_blank', 'noopener'); } catch { /* ignore */ }

    setSelectedAmount(null);
    setPayAccount(null);
    setShowPleaseWait(true);
    setTimeout(() => {
      setShowPleaseWait(false);
      navigate('/profile?openHistory=1');
    }, 2500);
  };

  const canPay = !!selectedGame && !!selectedMethod && !!selectedAmount;
  const methodOptions: PaymentMethod[] = [WALLET_OPTION, ...paymentMethods];

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <PageHeader icon="arrow-circle-up" title="Top-Up" />

      {loading ? (
        <div style={{ padding: 20 }}><SkeletonForm fields={4} /></div>
      ) : (
        <div style={{ padding: 20, paddingBottom: 40 }}>
          <SectionLabel>1. Select Game</SectionLabel>
          <Dropdown
            icon="gamepad"
            text={selectedGame?.name || ''}
            placeholder="Select a game"
            onClick={() => setShowGamePicker(true)}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <SectionLabel>2. Payment Method</SectionLabel>
            {isWallet && (
              <div style={{
                fontSize: 11, fontWeight: 800, padding: '4px 10px',
                borderRadius: 999, color: colors.accentText,
                backgroundColor: colors.accentSoft, marginTop: 18,
              }}>
                Bal: ${walletBalance.toFixed(2)}
              </div>
            )}
          </div>

          {walletError && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              borderRadius: 14, border: `1px solid ${colors.cardBorder}`,
              backgroundColor: colors.accentSoft,
              padding: 12, marginBottom: 12,
            }}>
              <Icon name="wallet" size={14} color={colors.accentText} />
              <div style={{ flex: 1, fontSize: 12, lineHeight: 1.45, fontWeight: 600, color: colors.accentText }}>
                {walletError}
              </div>
              <button
                onClick={() => navigate('/wallet-topup')}
                style={{
                  borderRadius: 999, padding: '7px 10px', backgroundColor: colors.accent,
                  border: 'none', color: '#020617', fontSize: 11, fontWeight: 800, cursor: 'pointer',
                }}
              >
                Top Up Wallet
              </button>
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {methodOptions.map(m => {
              const isSelected = selectedMethod?.id === m.id;
              const isWalletPill = m.id === WALLET_OPTION.id;
              return (
                <Chip
                  key={m.id}
                  active={isSelected}
                  onClick={() => { setWalletError(''); setSelectedMethod(m); }}
                  label={m.name}
                  icon={
                    isWalletPill ? (
                      <Icon name="wallet" size={13} color={isSelected ? '#020617' : colors.accentText} />
                    ) : m.logoUrl ? (
                      <img src={m.logoUrl} alt="" style={{ width: 18, height: 18, borderRadius: 4, objectFit: 'contain' }} />
                    ) : (
                      <Icon name="credit-card" size={13} color={isSelected ? '#020617' : colors.textSub} />
                    )
                  }
                />
              );
            })}
          </div>

          <SectionLabel>3. Select Amount</SectionLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {AMOUNT_OPTIONS.map(amt => (
              <AmountChip key={amt} amount={amt} active={selectedAmount === amt} onClick={() => setSelectedAmount(amt)} />
            ))}
          </div>

          {canPay && (
            <SummaryCard title="Top-Up Summary">
              <SummaryRow label="Game" value={selectedGame?.name || ''} />
              <SummaryRow label="Payment Method" value={selectedMethod?.name || ''} />
              <SummaryRow label="Amount" value={`$${selectedAmount}`} />
              <SummaryRow label="Processing Time" value={isWallet ? 'Instant' : '5–30 min'} />
            </SummaryCard>
          )}

          <PayButton
            label={isWallet ? 'Pay from Wallet' : 'Continue to Payment'}
            onClick={handlePayNow}
            loading={submitting}
            disabled={!canPay}
          />
        </div>
      )}

      <Modal open={showPayModal} onClose={() => setShowPayModal(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>Confirm Payment</div>
        <div style={{
          textAlign: 'center', padding: 20, borderRadius: 14,
          backgroundColor: colors.bg, border: `1px solid ${colors.cardBorder}`, marginBottom: 16,
        }}>
          <div style={{ fontSize: 36, fontWeight: 900, color: colors.text }}>${selectedAmount?.toFixed(2)}</div>
          <div style={{ fontSize: 14, color: colors.textSub, marginTop: 4 }}>via {selectedMethod?.name}</div>
          {payAccount && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
              <Icon name="link" size={12} color={colors.textSub} />
              <div style={{ fontSize: 13, fontWeight: 600, color: colors.textSub }}>{payAccount.label}</div>
            </div>
          )}
        </div>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          backgroundColor: colors.accentSoft, borderRadius: 12, padding: 12, marginBottom: 20,
        }}>
          <Icon name="info-circle" size={13} color={colors.accentText} style={{ marginTop: 2 }} />
          <div style={{ flex: 1, fontSize: 12, lineHeight: 1.5, color: colors.accentText }}>
            Tapping "Open & Pay" will launch the payment app. Complete the payment there, then come back.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label="Cancel" variant="outline" onClick={() => setShowPayModal(false)} />
          <Button label="Open & Pay" icon="external-link-alt" onClick={handleConfirmPay} />
        </div>
      </Modal>

      <Modal open={showGamePicker} onClose={() => setShowGamePicker(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>Select Game</div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {games.map(g => (
            <button
              key={g.id}
              onClick={() => { setSelectedGame(g); setShowGamePicker(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '13px 0', borderBottom: `1px solid ${colors.divider}`,
                background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                minHeight: 50,
              }}
            >
              <Icon name="gamepad" size={14} color={selectedGame?.id === g.id ? colors.accentDark : colors.textSub} />
              <div style={{
                flex: 1, fontSize: 15,
                color: selectedGame?.id === g.id ? colors.accentDark : colors.text,
                fontWeight: selectedGame?.id === g.id ? 700 : 400,
              }}>
                {g.name}
              </div>
              {selectedGame?.id === g.id && <Icon name="check" size={13} color={colors.accentDark} />}
            </button>
          ))}
        </div>
        <Button label="Cancel" variant="outline" onClick={() => setShowGamePicker(false)} style={{ marginTop: 12 }} />
      </Modal>

      <PleaseWait
        open={showPleaseWait}
        title="Order Recorded!"
        message={
          isWallet
            ? 'Wait a sec while your order moves to history. Admin will load your game next.'
            : 'Wait a sec while your payment is being verified. You can track it in Order History.'
        }
      />
    </div>
  );
}
